import React, { useState, useEffect, useRef } from "react";
import { useDrag } from "react-use-gesture";
import BarLoader from "react-spinners/BarLoader";

import Stage from "../Stage/stage";
import { useInterval } from "../../hooks/useInterval";
import Center from "../Center/Center";
import Popup from "../../../Popup"; // Import the Popup component

import { PrintPlayerInMap } from "../../utils/Utils";
import { updateDocumentInSubcollection } from '../../../../firestore_sub'; // Import Firestore update function
import { auth } from '../../../../firebase'; // Import Firebase auth to get UID
import { updateDocument, getDocument } from '../../../../firestore'; // Import Firestore update function

const STAGE_HEIGHT = 18;
const STAGE_WIDTH = 10;

const initialMap = [...new Array(STAGE_HEIGHT)].map(() =>
  [...new Array(STAGE_WIDTH)].map(() => ({ fill: 0, color: [] }))
);

const colors = [
  "#e54b4b",
  "#9a031e",
  "#fcdc4d",
  "#005397",
  "#0bbcd6",
  "#20ad65",
  "#f8ebee"
];

const I = {
  bloco: [
    [0, 0, 0, 0],
    [1, 1, 1, 1],
    [0, 0, 0, 0],
    [0, 0, 0, 0]
  ]
};

const O = {
  bloco: [
    [1, 1],
    [1, 1]
  ]
};

const T = {
  bloco: [
    [0, 0, 0],
    [1, 1, 1],
    [0, 1, 0]
  ]
};

const J = {
  bloco: [
    [0, 1, 0],
    [0, 1, 0],
    [1, 1, 0]
  ]
};

const L = {
  bloco: [
    [0, 1, 0],
    [0, 1, 0],
    [0, 1, 1]
  ]
};

const S = {
  bloco: [
    [0, 1, 1],
    [1, 1, 0],
    [0, 0, 0]
  ]
};

const Z = {
  bloco: [
    [1, 1, 0],
    [0, 1, 1],
    [0, 0, 0]
  ]
};

const getRandomBloco = () => {
  const blocos = [I, O, T, J, L, S, Z];
  const bloco = blocos[Math.floor(Math.random() * blocos.length)];
  bloco.color = colors[Math.floor(Math.random() * colors.length)];
  return bloco;
};

const getRandomPlayer = (player) => {
  let bloco, next;
  if (player)
    if (player.next) {
      bloco = JSON.parse(JSON.stringify(player.next));
      next = getRandomBloco();
    }
  if (!bloco) bloco = getRandomBloco();
  if (!next) next = getRandomBloco();
  const pos = [0, Math.floor(STAGE_WIDTH / 2 - 2 / 2)];
  return { pos, bloco, next };
};

const Game = () => {
  const [map, setMap] = useState(initialMap);
  const [player, setPlayer] = useState();
  const [down, setDown] = useState(false);
  const [pause, setPause] = useState(false);
  const [tick, setTick] = useState(Date.now());
  const [hintPlayer, setHintPlayer] = useState();
  const [spaceReleased, setSpaceReleased] = useState(true);
  const [lines, setlines] = useState(0);
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [dragX, setDragX] = useState(0);
  const [dragY, setDragY] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [lives, setLives] = useState(3); // Add lives state

  const [showPopup, setShowPopup] = useState(false); // To control popup visibility
  const uid = auth.currentUser ? auth.currentUser.uid : null;

  const gameAreaRef = useRef(null); // Create a ref for the game area

  useEffect(() => {
    if (gameAreaRef.current) {
      gameAreaRef.current.focus();
    }
  }, []);

  useEffect(() => {
    const levelBaseScore = 1000;
    const nextLevel = level + 1;
    const nextLevelScore =
      (levelBaseScore * nextLevel * nextLevel * nextLevel) / 5;
    console.log("Current level: ", level);
    console.log("Score to next level:", nextLevelScore);
    console.log("Remaining: ", nextLevelScore - score);
    if (score >= nextLevelScore) setLevel(level + 1);
  }, [level, score]);

  const loseGame = () => {
    setGameOver(true);
  };

    // Refocus the game container when the popup closes
  useEffect(() => {
    if (!showPopup && gameAreaRef.current) {
      gameAreaRef.current.focus(); // Automatically refocus game container
      setPause(false); // Resume game
    }
  }, [showPopup]);

  const handlePopupClose = () => {
    setPause(false); // Resume game when popup closes
    setShowPopup(false);

    // Ensure the game area is focused again after the popup closes
    if (gameAreaRef.current) {
      gameAreaRef.current.focus();
    }
  };

  const drop = () => {
    if (!player) {
      setPlayer(getRandomPlayer());
      return;
    }
    setPlayer((player) => {
      const newPos = getNewPlayerPos("down");
      if (player.pos === newPos) {
        setMap((map) => {
          const mapWithPlayer = PrintPlayerInMap(player, map);
          const mapCleared = checkMap(mapWithPlayer);
          return mapCleared;
        });
        const newPlayer = getRandomPlayer(player);
        if (!validatePosition(newPlayer.pos, newPlayer.bloco)) loseGame();
        return newPlayer;
      }
      return { ...player, pos: newPos };
    });
  };

  const rotatePlayer = () => {
    const clonedPlayer = JSON.parse(JSON.stringify(player));
    let mtrx = clonedPlayer.bloco.bloco.map((_, index) =>
      clonedPlayer.bloco.bloco.map((column) => column[index])
    );
    mtrx = mtrx.map((row) => row.reverse());
    if (validatePosition(player.pos, { bloco: mtrx }))
      setPlayer({ ...player, bloco: { ...player.bloco, bloco: mtrx } });
  };

  const keyUp = ({ keyCode }) => {
    if (pause || gameOver) return;
    const THRESHOLD = 80;
    // Activate the interval again when user releases down arrow.
    if (keyCode === 40) {
      setDown(false);
      if (Date.now() - tick <= THRESHOLD) drop();
    }
    if (keyCode === 32) setSpaceReleased(true);
  };

  const forwardDown = () => {
    if (pause || gameOver) return;
    setPlayer((player) => {
      const playerCopy = JSON.parse(JSON.stringify(player));
      playerCopy.pos = [...hintPlayer.pos];
      setMap((map) => {
        const mapWithPlayer = PrintPlayerInMap(playerCopy, map);
        const mapCleared = checkMap(mapWithPlayer);
        return mapCleared;
      });
      const newPlayer = getRandomPlayer(player);
      if (!validatePosition(newPlayer.pos, newPlayer.bloco)) loseGame();
      return newPlayer;
    });
  };

  const keyDown = (event) => {
    const { keyCode } = event;

    if (pause || gameOver) return;

    // Prevent default behavior for arrow keys and spacebar
    if ([32, 37, 38, 39, 40].includes(keyCode)) {
      event.preventDefault();
    }

    switch (keyCode) {
      case 37: // Left arrow
        setPlayer((player) => ({ ...player, pos: getNewPlayerPos("left") }));
        break;
      case 38: // Up arrow (rotate)
        rotatePlayer();
        break;
      case 39: // Right arrow
        setPlayer((player) => ({ ...player, pos: getNewPlayerPos("right") }));
        break;
      case 40: // Down arrow (drop faster)
        setTick(Date.now());
        setDown(true);
        break;
      case 32: // Space (fast forward down)
        if (spaceReleased) {
          setSpaceReleased(false);
          forwardDown();
        }
        break;
      default:
        break;
    }
  };

  const checkMap = React.useCallback(
    (map) => {
      let rowsClear = [];
      map.forEach((row, y) => {
        let clear = true;
        row.forEach((pixel, x) => {
          if (pixel.fill === 0) clear = false;
        });
        if (clear) rowsClear.push(y);
      });
      if (rowsClear.length > 0) {
        let newMap = map.slice();
        rowsClear.forEach((y) => {
          for (let mapY = newMap.length - 1; mapY >= 0; mapY--)
            if (mapY <= y)
              if (mapY > 0) newMap[mapY] = newMap[mapY - 1];
              else
                newMap[mapY] = [...new Array(STAGE_WIDTH)].map(() => ({
                  fill: 0,
                  color: []
                }));
        });
        setlines((quant) => quant + rowsClear.length);
        const bonusLevel = 100 * level * level;
        const bonusRows = 40 * (rowsClear.length * rowsClear.length - 1);
        setScore(
          (score) =>
            score + 300 * rowsClear.length + bonusRows + bonusLevel
        );
        return newMap;
      }
      return map;
    },
    [level]
  );

   // Firestore update for Tetris Game data
  //  useEffect(() => {
  //   if (uid) {
  //     const updatedData = {
  //       game_id: "TetrisGame",
  //       played_at: new Date().toUTCString(),
  //       score: score,
  //       lines_cleared: lines,
  //       level: level,
  //       lives: lives,
  //       uid: uid,
  //     };

  //     updateDocumentInSubcollection("gameplay", uid, "gameplays", "TetrisGame", updatedData)
  //       .then(() => console.log("TetrisGame document successfully updated!"))
  //       .catch((error) => console.error("Error updating TetrisGame document:", error));
  //   }
  // }, [score, lines, level, lives, uid]);

  const validatePosition = React.useCallback(
    (pos, bloco) => {
      for (let y = 0; y < bloco.bloco.length; y++)
        for (let x = 0; x < bloco.bloco[y].length; x++)
          if (bloco.bloco[y][x] === 1) {
            let mapY = pos[0] + y;
            let mapX = pos[1] + x;
            if (
              mapY > STAGE_HEIGHT ||
              mapX < 0 ||
              mapX > STAGE_WIDTH ||
              !map[mapY] ||
              !map[mapY][mapX] ||
              map[mapY][mapX].fill === 1
            )
              return false;
          }
      return true;
    },
    [map]
  );

  const calculateHintPlayer = React.useCallback(
    (player) => {
      const hintBloco = JSON.parse(JSON.stringify(player.bloco));
      let hintPosition = [...player.pos];
      while (validatePosition([hintPosition[0] + 1, hintPosition[1]], hintBloco))
        hintPosition = [hintPosition[0] + 1, hintPosition[1]];
      return { pos: hintPosition, bloco: hintBloco };
    },
    [validatePosition]
  );

  const getNewPlayerPos = React.useCallback(
    (movement) => {
      let newPos;
      if (!player) return;
      if (movement === "down") newPos = [player.pos[0] + 1, player.pos[1]];
      if (movement === "left") newPos = [player.pos[0], player.pos[1] - 1];
      if (movement === "right") newPos = [player.pos[0], player.pos[1] + 1];
      if (!validatePosition(newPos, player.bloco)) return player.pos;
      return newPos;
    },
    [player, validatePosition]
  );

  useInterval(
    () => {
      drop();
    },
    pause || gameOver ? null : down ? 50 : 450 - (level - 1) * 20
  );

  useEffect(() => {
    if (!player) return;
    setHintPlayer(calculateHintPlayer(player));
  }, [player, calculateHintPlayer]);

  const bind = useDrag(
    ({ down, movement: [mx, my], velocity }) => {
      const THRESHOLD = 20;
      const FORCE_THRESHOLD = 1;
      if (down) {
        if (Math.abs(mx - dragX) > THRESHOLD) {
          if (mx - dragX > 0)
            setPlayer((player) => ({
              ...player,
              pos: getNewPlayerPos("right")
            }));
          else setPlayer((player) => ({ ...player, pos: getNewPlayerPos("left") }));
          setDragX(mx);
        }
        if (Math.abs(my - dragY) > THRESHOLD) {
          if (velocity > FORCE_THRESHOLD) {
            if (spaceReleased) {
              setSpaceReleased(false);
              forwardDown();
            }
          } else if (my - dragY > 0) drop();
          setDragY(my);
        }
      } else {
        setDragX(0);
        setDragY(0);
        setSpaceReleased(true);
      }
    },
    { filterTaps: true, lockDirection: true }
  );
  
  useEffect(() => {
    // Show popup at intervals
    if (!gameOver && lives > 0 && !showPopup) {
      const timer = setTimeout(() => {
        setPause(true); // Pause the game
        setShowPopup(true); // Show popup
      }, 20000); // Show popup every 20 seconds
      return () => clearTimeout(timer);
    }

    if (lives <=0){
      gameOverDBUpdate();
      setGameOver(true);
    }
  }, [gameOver, lives, showPopup]);

  async function checkGameStarted() {
    const queryParams = new URLSearchParams(window.location.search);
    const gameId = queryParams.get("id");
    if (!gameId) return; 
  
    const userId = auth.currentUser.uid;
    const subCollectionPath = `gameplay/${userId}/gameplays/`;
    
    try {
      const gameDoc = await getDocument(subCollectionPath, gameId);
      if (gameDoc?.gameStarted) {
        const redirectUrl = `${window.location.origin}/instructions/2048`;
        window.location.href = redirectUrl;
      }
    } catch (error) {
      console.error("Error checking game status:", error);
    }
  }
  

  async function gameOverDBUpdate()
  {
      const queryParams = new URLSearchParams(window.location.search);
      var gameId = queryParams.get("id");
      const userId = auth.currentUser.uid;
      const subCollectionPath = `gameplay/${userId}/gameplays/`;
      let currentScore = score;
     const gameplayData = {
        lives: lives,
        score: currentScore,
        // total_questions : total_questions
      };
      await updateDocument(subCollectionPath, gameId, gameplayData);
      await totalGameDataUpdate(currentScore);
    }
  
    async function totalGameDataUpdate(score)
    {
      const subCollectionPath = `gameplay`;
      const userId = auth.currentUser.uid;
      var docData = await getDocument(subCollectionPath, userId);
      let oldScore = docData.score;
      let total_games = docData.games_played+1;
      let currentScore = score+oldScore;
     const gameplayData = {
        games_played: total_games,
        score: currentScore,
        // total_questions : total_questions
      };
      await updateDocument(subCollectionPath, userId, gameplayData);
    }
    

  useEffect(() => {
    const focusTimeout = setTimeout(() => {
      if (gameAreaRef.current) {
        gameAreaRef.current.focus();
      }
    }, 100); // 100 ms delay
  
    return () => clearTimeout(focusTimeout);
  }, []);
  
  if (!player || !map || !hintPlayer)
    return (
      <Center>
        <BarLoader color={"#C41212"} />
      </Center>
    );

    const restartGame = () => {
      setMap(initialMap);
      setlines(0);
      setScore(0);
      setLevel(1);
      setGameOver(false);
      setLives(3); // Reset lives
  
      // Refocus the game area after restarting
      if (gameAreaRef.current) {
        gameAreaRef.current.focus();
      }
    };
  return (
    <div
      ref={gameAreaRef}
      tabIndex={0}
      onKeyUp={keyUp}
      onKeyDown={keyDown}
      style={{ outline: "none", maxWidth: "1200px", margin: "0 auto" }} // Added maxWidth and margin
    >
      <input
      type="text"
      style={{ position: "absolute", opacity: 0, pointerEvents: "none" }}
      autoFocus
    />
      <div style={{ display: 'flex', justifyContent: 'center'  }}>
    
</div>

      <Stage
        lose={gameOver}
        restartClick={restartGame}
        map={map}
        player={player}
        hint={hintPlayer}
        paused={pause}
        status={{ lines, score, level, lives }}
        onClick={() => rotatePlayer()}
        {...bind()}
      />

      {showPopup && (
        <Popup
          onClose={handlePopupClose}
          onIncorrectAnswer={() => setLives((prevLives) => prevLives - 1)}
          score={score}
          setScore={setScore}
          lives={lives}
          setLives={setLives}
          useLives={true}
          gameOver={gameOver}
        />
      )}
    </div>
  );
};

export default Game;
